/**
 * @documental
 * @export SubmitButton
 */
import { FC, PropsWithChildren, RefCallback, useMemo } from "react";

import { SubmitGroup } from "capitalrx-components-2.0/lib/forms/SubmitGroup";
import { Button, ButtonProps } from "capitalrx-components-2.0/lib/general/Button";
import { Placeholder } from "capitalrx-components-2.0/lib/general/Button/Placeholder";
import { getAppearanceForString, getCustomAppearanceStyle } from "capitalrx-components-2.0/lib/general/Button/helpers";
import buttonTokenStyles from "capitalrx-components-2.0/lib/general/Button/styles.module.css";
import { useDisabled } from "capitalrx-components-2.0/lib/general/DisableGroup";
import { Icon } from "capitalrx-components-2.0/lib/general/Icon";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general/LoadingGroup";
import { Spinner } from "capitalrx-components-2.0/lib/general/Spinner";
import { Tooltip } from "capitalrx-components-2.0/lib/general/Tooltip";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { Semibold } from "capitalrx-components-2.0/lib/typography";
import { getHtmlPropsFromBaseProps } from "capitalrx-components-2.0/lib/utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "capitalrx-components-2.0/lib/utilities/withSubComponents";

import tokenStyles from "./styles.module.css";

/**
 * The `SubmitButton` component properties.
 */

export interface SubmitButtonProps extends Omit<ButtonProps, "onClick" | "tabIndex"> { }

/**
 * The `SubmitButton` component sub components.
 */
export interface SubmitButtonSubComponents {
  /**
   * A placeholder component for the Button which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

const useResolvedSubmitButtonStyles = createComponentStyleHook<SubmitButtonProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "button",
});

/**
 * A form submit button.
 *
 * `import { SubmitButton } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>Example use case:</strong>
 *
 * `<SubmitButton {...props}>Submit</SubmitButton>;`
 */
export const SubmitButton = withSubComponents<FC<PropsWithChildren<SubmitButtonProps>>, SubmitButtonSubComponents>(
  {
    Placeholder,
  },
  (props) => {
    const {
      appearance = "primary",
      size = "large",
      icon,
      iconPosition = "left",
      children,
      tooltip,
      autoFocus = false,
      transparent = false,
      "aria-busy": ariaBusy,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedby,
    } = props;
    const { mode, styles, StyleModeBoundary } = useResolvedSubmitButtonStyles(props);
    const iconOnly = !!(icon && !children);

    if (process.env.NODE_ENV !== "production" && iconOnly && !props["aria-label"]) {
      console.warn(
        `SubmitButton: Icon-only buttons should have an 'aria-label' for accessibility.\n`
        + `Button with icon "${icon}" is missing an aria-label.\n`
        + `Screen reader users won't know what this button does.\n`
        + `Example: <SubmitButton icon="${icon}" aria-label="Descriptive action text" />`,
      );
    }

    const disabled = useDisabled(props);

    const isStringAppearance = typeof appearance === "string";
    const appearanceData = isStringAppearance ? getAppearanceForString(appearance, transparent, size) : appearance;
    const resolvedSize = isStringAppearance ? size : (appearanceData.className?.match(/\b(large|small|compact)\b/)?.[1] ?? size);

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.SubmitButton,
        buttonTokenStyles.Button,
        "submit-button",
        isStringAppearance ? appearance : "custom",
        resolvedSize,
        {
          transparent,
          "icon-only": iconOnly,
          disabled,
        },
      ],
      testIdPrefix: "submit-button",
    });

    const submitContent = (
      <Shelf spacing="small">
        {icon && (iconPosition === "left" || iconOnly) && (
          <span className={buttonTokenStyles.Icon}>
            <Icon name={icon} size="regular" />
          </span>
        )}
        {!iconOnly && <Semibold>{children ?? <>&nbsp;</>}</Semibold>}
        {icon && iconPosition === "right" && !iconOnly && (
          <span className={buttonTokenStyles.Icon}>
            <Icon name={icon} size="regular" />
          </span>
        )}
      </Shelf>
    );

    const sharedSubmitProps = {
      disabled,
      type: "submit" as const,
      autoFocus,
      "aria-busy": ariaBusy,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedby,
    };

    const customStyle = useMemo(
      () => (!isStringAppearance ? getCustomAppearanceStyle(appearanceData, iconOnly) : undefined),
      [isStringAppearance, appearanceData, iconOnly],
    );

    const content = (ref?: RefCallback<HTMLButtonElement>) => (
      <button {...baseHtmlProps} {...sharedSubmitProps} style={{ ...baseHtmlProps.style, ...customStyle }} ref={ref}>
        {submitContent}
      </button>
    );

    return (
      <StyleModeBoundary mode={mode}>
        <LoadingGroup.Placeholder
          fallback={
            <Placeholder className={props.className} style={props.style} appearance={appearanceData} icon={!!icon}>
              {children}
            </Placeholder>
          }
        >
          <SubmitGroup.Placeholder
            fallback={
              <Button
                className={styles.SubmitPlaceholder}
                transparent={transparent}
                appearance={appearance}
                size={size}
                tooltip={tooltip}
                tabIndex={-1}
                aria-busy={ariaBusy}
                aria-describedby={ariaDescribedby}
                disabled
              >
                <Shelf spacing="small">
                  <Spinner size="regular" />
                  <span>Submitting</span>
                </Shelf>
              </Button>
            }
          >
            {tooltip && <Tooltip content={tooltip}>{content}</Tooltip>}
            {!tooltip && content()}
          </SubmitGroup.Placeholder>
        </LoadingGroup.Placeholder>
      </StyleModeBoundary>
    );
  },
);
